import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialsSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const sectionRef = useRef(null);
    const autoPlayRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Auto-play functionality
    useEffect(() => {
        if (isAutoPlaying) {
            autoPlayRef.current = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % testimonials.length);
            }, 5000);
        }

        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [isAutoPlaying]);

    const testimonials = [
        {
            id: 1,
            rating: 5,
            text: "Fast payment processing and transparent billing have made RoadExpress our preferred logistics partner. No more payment delays!",
            author: "Kishan Giri",
            position: "Transport Manager",
            company: "R Sai Logistics"
        },
        {
            id: 2,
            rating: 5,
            text: "Good payment terms and reliable reverse logistics support. RoadExpress has streamlined our return shipment process significantly.",
            author: "Suneel Jhagra",
            position: "Operations Head",
            company: "Velocity"
        },
        {
            id: 3,
            rating: 5,
            text: "Timely payments and excellent load availability have helped us maximize our fleet utilization. Highly recommend RoadExpress!",
            author: "Ragini",
            position: "Director",
            company: "Sure Cargo Logistics"
        },
        {
            id: 4,
            rating: 5,
            text: "Quick payment cycles and real-time tracking have improved our cash flow. The platform is user-friendly and efficient.",
            author: "Suresh Kumar",
            position: "Fleet Owner",
            company: "Varuna"
        },
        {
            id: 5,
            rating: 5,
            text: "Fast payment processing and consistent load availability keep our trucks moving. RoadExpress understands carrier needs perfectly.",
            author: "Jatin Agarwal",
            position: "Managing Director",
            company: "Agarwal Transport"
        },
        {
            id: 6,
            rating: 5,
            text: "Good payment system and dedicated support for reverse logistics. RoadExpress has been a game-changer for our operations.",
            author: "Dharmender",
            position: "Transport Head",
            company: "Siliguri Guwahati Transport"
        },
        {
            id: 7,
            rating: 5,
            text: "Reliable payments on time, every time. The digital documentation and automated billing have simplified our workflow completely.",
            author: "Sachidananda",
            position: "Operations Manager",
            company: "SS Carrier"
        },
        {
            id: 8,
            rating: 5,
            text: "Fast payment clearance and excellent reverse logistics support. RoadExpress has helped us reduce empty return trips significantly.",
            author: "Ramesh Kumar",
            position: "Director",
            company: "SS Cargo"
        },
        {
            id: 9,
            rating: 5,
            text: "Prompt payments and great platform for finding return loads. The transparency in pricing and payment tracking is outstanding.",
            author: "Pankaj Sharma",
            position: "Fleet Manager",
            company: "Pkart Transport"
        }
    ];

    const nextTestimonial = () => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        setIsAutoPlaying(false);
    };

    const prevTestimonial = () => {
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
        setIsAutoPlaying(false);
    };

    const goToTestimonial = (index) => {
        setCurrentIndex(index);
        setIsAutoPlaying(false);
    };

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`w-5 h-5 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                    }`}
            />
        ));
    };

    return (
        <section
            ref={sectionRef}
            className="w-full py-20 overflow-hidden"
            aria-labelledby="testimonials-section-title"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Section Header */}
                <div
                    className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                >
                    <h2
                        id="testimonials-section-title"
                        className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
                    >
                        What Our Carriers Say
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Trusted by thousands of carriers across India. Here's what our partner carriers have to say about RoadExpress.
                    </p>
                </div>

                {/* Testimonial Carousel Container */}
                <div className="relative">
                    {/* Main Testimonial Card */}
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.3s' }}
                    >
                        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mx-auto max-w-4xl relative overflow-hidden">
                            {/* Quote Icon */}
                            <div className="absolute top-6 left-6 text-[#F05A28]/20">
                                <Quote className="w-16 h-16" />
                            </div>

                            {/* Rating Stars */}
                            <div className="flex justify-center mb-6 relative z-10">
                                <div className="flex space-x-1">
                                    {renderStars(testimonials[currentIndex].rating)}
                                </div>
                            </div>

                            {/* Testimonial Text */}
                            <div className="text-center mb-8 relative z-10">
                                <p className="text-xl md:text-xl lg:text-2xl font-medium text-gray-800 leading-relaxed mb-8 italic">
                                    "{testimonials[currentIndex].text}"
                                </p>
                            </div>

                            {/* Author Info */}
                            <div className="flex items-center justify-center relative z-10">
                                <div className="text-center">
                                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                                        {testimonials[currentIndex].author}
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        {testimonials[currentIndex].position}, {testimonials[currentIndex].company}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center justify-center space-x-4 mt-8">
                        {/* Dots Indicator */}
                        <div className="flex space-x-2">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToTestimonial(index)}
                                    className={`w-3 h-2 rounded-full transition-all duration-300 ${index === currentIndex
                                        ? 'bg-[#F05A28] w-8'
                                        : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    aria-label={`Go to testimonial ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* SEO Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "RoadExpress",
                    "review": testimonials.map(testimonial => ({
                        "@type": "Review",
                        "author": {
                            "@type": "Person",
                            "name": testimonial.author
                        },
                        "reviewBody": testimonial.text,
                        "reviewRating": {
                            "@type": "Rating",
                            "ratingValue": testimonial.rating,
                            "bestRating": 5
                        }
                    }))
                })}
            </script>
        </section>
    );
};

export default TestimonialsSection;